import {
  useCallback,
  useContext,
  useMemo,
  useState,
  createContext,
} from "react";
import { FileType } from "../../Files/FileListItem/__generated__/FileIcon_File.graphql";
import { Connection, Section, Task } from "./Routine/types";
import { Team } from "@/src/types/Team";
import { graphql, useFragment } from "react-relay";
import {
  RoutineEditStateProvider_routine$data,
  RoutineEditStateProvider_routine$key,
} from "./__generated__/RoutineEditStateProvider_routine.graphql";
import { getControlType } from "../RoutineEditMain/RoutineEditControlTypeCard/getControlType";
import { DayFilter, dayFilterToDays } from "../../TeamScheduleAdmin/filter";
import { RoutineEditParams } from "../RoutineEditMain/RoutineEditNavigation";
import { ALL_WEEK_NUMBERS } from "../RoutineEditMain/RoutineEditScheduleCard/WeekNumberPickerDialog";

type RoutineEditChecklistSelectedState =
  | { type: "SECTION"; id: string; autoFocus?: boolean }
  | { type: "TASK"; taskId: string; sectionId: string; autoFocus?: boolean }
  | null;

type RoutineEditChecklistState = {
  selected: RoutineEditChecklistSelectedState;
  sections: Section[];
  connections: Connection[];
  tasks: Task[];
};

type RoutineScheduleDaily = {
  freq: "daily";
  isAllDay: boolean;
  deadlineDuration: number | null;
  weekdays: {
    weekday: number;
    startTime: {
      hour: number;
      minute: number;
    } | null;
    originalTaskImportance: Record<string, "REQUIRED" | "OPTIONAL">;
    taskImportanceChanges: Record<string, "REQUIRED" | "OPTIONAL" | null>;
  }[];
};

type RoutineScheduleWeekly = {
  freq: "weekly";
  startWeekday: number;
  endWeekday: number;
  weeks: number[];
};

type RoutineScheduleMonthly = {
  freq: "monthly";
  months: number[];
  startDay: number;
  endDay: number;
};

export type ControlType = "none" | "check" | "checklist";
type ScheduleFreq = "daily" | "weekly" | "monthly";
export type RoutineEditState = {
  title: string;
  remoteTitle: string;
  description?: string;
  removeDescriptionMediaIds: string[];
  addDescriptionMedia: {
    id: string;
    uri: string;
    width: number;
    height: number;
  }[];
  removeDescriptionFileIds: string[];
  addDescriptionFiles: {
    id: string;
    name: string;
    type: FileType | null;
    url: string;
  }[];
  scheduleFreq?: ScheduleFreq;
  scheduleDaily?: RoutineScheduleDaily;
  scheduleWeekly?: RoutineScheduleWeekly;
  scheduleMonthly?: RoutineScheduleMonthly;
  team?: Team;
  controlType: ControlType;
  assignedRoleIds?: string[];
} & Partial<RoutineEditChecklistState>;

const DEFAULT_STATE: RoutineEditState = {
  removeDescriptionMediaIds: [],
  addDescriptionMedia: [],
  removeDescriptionFileIds: [],
  addDescriptionFiles: [],
  remoteTitle: "",
  title: "",
  controlType: "none",
};

const CTX = createContext<{
  state: RoutineEditState;
  setState: React.Dispatch<React.SetStateAction<RoutineEditState>>;
}>({
  state: DEFAULT_STATE,
  setState: () => {},
});

export function useRoutineEditState() {
  return useContext(CTX).state;
}
export function useRoutineEditStateSetParams() {
  const setState = useContext(CTX).setState;

  return (params: Partial<RoutineEditState>) => {
    setState((prev) => ({ ...prev, ...params }));
  };
}

export function useRoutineEditStateSetParamsFunction() {
  const setState = useContext(CTX).setState;

  return useCallback(
    (params: (prev: RoutineEditState) => RoutineEditState) => {
      setState((prev) => params(prev));
    },
    [setState]
  );
}

type RemoteSection =
  RoutineEditStateProvider_routine$data["activeVersion"]["sections"][number];
type RemoteTask =
  RoutineEditStateProvider_routine$data["activeVersion"]["tasks"][number];
type RemoteConnection =
  RoutineEditStateProvider_routine$data["activeVersion"]["connections"][number];

const remoteSectionToLocal: (_: RemoteSection) => Section = (s) => {
  return {
    id: s.section.id,
    ordinal: s.ordinal,
    title: s.title,
    assignedTeam: s.section.assignedTeam,
    orgTopic: s.orgTopic,
    orgCanBeDisabled: s.orgCanBeDisabled,
  };
};

const remoteTaskToLocal: (_: RemoteTask) => Task = (t) => {
  const baseTask = {
    id: t.id,
    title: t.title ?? "",
    instructions: t.instructions ?? "",
    images: t.images.map((img) => ({
      id: img.id,
      uri: img.size?.url ?? "",
      width: img.size?.width ?? 0,
      height: img.size?.height ?? 0,
    })),
  };
  switch (t.__typename) {
    case "TaskText": {
      return { ...baseTask, type: "TaskText" };
    }
    case "TaskNumber": {
      return {
        ...baseTask,
        type: "TaskNumber",
        goal: t.goals?.[0]?.lessThan
          ? "max"
          : t.goals?.[0]?.greaterThan
            ? "min"
            : undefined,
        unit: t.unit ?? "CUSTOM",
        suffix: "",
      };
    }
    case "TaskCheck": {
      return {
        ...baseTask,
        type: "TaskCheck",
        sentiments: t.sentiments!.flatMap((s) => (s !== undefined ? [s] : [])),
      };
    }
  }
  throw Error("Task __typename not handled");
};

const remoteConnectionToLocal: (
  connection: RemoteConnection,
  tasks: RoutineEditStateProvider_routine$data["activeVersion"]["tasks"]
) => Connection = (connection, tasks) => {
  const t = tasks.find(
    (task) => task.id === connection.taskId && task.__typename === "TaskNumber"
  );

  const goal = t?.goals?.find((g) => g.section.id === connection.sectionId);
  const goalValue = goal?.lessThan ?? goal?.greaterThan ?? 0;
  return {
    ordinal: connection.ordinal,
    sectionId: connection.sectionId,
    taskId: connection.taskId,
    value: goalValue,
    orgTopic: connection.orgTopic ?? null,
    canBeDisabled: connection.canBeDisabled ?? false,
  };
};

export function RoutineEditStateProvider({
  children,
  routineKey,
  params,
}: {
  children: React.ReactNode;
  routineKey: RoutineEditStateProvider_routine$key;
  params?: RoutineEditParams;
}) {
  const routine = useFragment(
    graphql`
      fragment RoutineEditStateProvider_routine on Routine {
        id
        title
        ownerTeam {
          id
          name
          color
          icon
          isPrivate
        }
        assignedRoles {
          role {
            id
          }
        }
        activeVersion {
          tasks {
            id
            title
            instructions
            images {
              id
              size(minHeight: 700, minWidth: 700) {
                id
                url
                width
                height
              }
            }
            __typename
            ... on TaskCheck {
              sentiments
            }
            ... on TaskNumber {
              unit
              goals {
                section {
                  id
                }
                lessThan
                greaterThan
              }
            }
          }
          connections {
            sectionId
            taskId
            ordinal
            orgTopic {
              id
              name
              isPrivate
            }
            canBeDisabled
          }
          sections {
            id
            title
            ordinal
            orgCanBeDisabled
            orgTopic {
              id
              name
              isPrivate
            }

            section {
              id
              assignedTeam {
                id
                name
                icon
                color
                isPrivate
              }
            }
          }
        }
      }
    `,
    routineKey
  );
  const controlType = useMemo(
    () =>
      getControlType(
        routine.activeVersion.tasks.map((t) => ({
          type: t.__typename,
          title: t.title,
          sentiments: t.sentiments,
        }))
      ),
    [routine.activeVersion.tasks]
  );

  const tasks: Task[] = useMemo(
    () => routine.activeVersion.tasks.flatMap(remoteTaskToLocal),
    [routine.activeVersion.tasks]
  );

  const connections: Connection[] = useMemo(
    () =>
      routine.activeVersion.connections.map((c) =>
        remoteConnectionToLocal(c, routine.activeVersion.tasks)
      ),
    [routine.activeVersion.connections, routine.activeVersion.tasks]
  );

  const sections: Section[] = useMemo(
    () => routine.activeVersion.sections.map(remoteSectionToLocal),
    [routine.activeVersion.sections]
  );

  const initialState = useMemo(
    () => (params ? getRoutineEditCreateInitialParams(params) : undefined),
    [params]
  );

  const assignedRoleIds = useMemo(
    () =>
      routine.assignedRoles
        .map((ar) => ar.role?.id)
        .filter((id): id is string => !!id),
    [routine.assignedRoles]
  );

  const initial = useMemo(
    (): RoutineEditState => ({
      ...DEFAULT_STATE,
      tasks,
      connections,
      sections,
      controlType,
      title: routine.title,
      remoteTitle: routine.title,
      team: routine.ownerTeam ?? undefined,
      assignedRoleIds,
      ...initialState,
    }),
    [
      tasks,
      connections,
      sections,
      controlType,
      routine.title,
      routine.ownerTeam,
      assignedRoleIds,
      initialState,
    ]
  );
  const [state, setState] = useState<RoutineEditState>(initial);
  return <CTX.Provider value={{ state, setState }}>{children}</CTX.Provider>;
}

function getRoutineEditCreateInitialParamsDaily(props: {
  selectedDaysFilter: DayFilter;
  startTime: { hour: number; minute: number } | null;
}) {
  const selectedDays = dayFilterToDays(props.selectedDaysFilter);
  return {
    deadlineDuration: null,
    isAllDay: props.startTime === null,
    freq: "daily" as const,
    weekdays: selectedDays.map((weekday) => ({
      weekday,
      startTime: props.startTime,
      originalTaskImportance: {},
      taskImportanceChanges: {},
    })),
  };
}

function getRoutineEditCreateInitialParams(
  props: RoutineEditParams
): Partial<RoutineEditState> | undefined {
  if (props.mode !== "CREATE") {
    return undefined;
  }
  const freq = props.createFreq;
  if (!freq) {
    return undefined;
  }

  let shared: Partial<RoutineEditState> = {
    scheduleFreq: freq,
  };
  if (props.orgWorkspaceId) {
    shared.controlType = "checklist";
  }
  if (props.createRoleId) {
    shared.assignedRoleIds = [props.createRoleId];
  }
  switch (freq) {
    case "daily":
      return {
        ...shared,
        scheduleDaily: getRoutineEditCreateInitialParamsDaily({
          selectedDaysFilter: props.createDailyDaysFilter ?? "all",
          startTime: props.createDailyStartTimeHour
            ? {
                hour: props.createDailyStartTimeHour,
                minute: props.createDailyStartTimeMinute ?? 0,
              }
            : null,
        }),
      };

    case "weekly":
      return {
        ...shared,
        scheduleWeekly: {
          freq: "weekly" as const,
          startWeekday: 0,
          endWeekday: 6,
          weeks: ALL_WEEK_NUMBERS,
        },
      };
    case "monthly":
      return {
        ...shared,
        scheduleMonthly: {
          freq: "monthly" as const,
          endDay: -1,
          startDay: 1,
          months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        },
      };
  }
  return undefined;
}
